export const AUTH_STORAGE_KEY = "smartstore_admin_auth_v1";
export const ACCESS_TOKEN_KEY = "accessToken";
export const REFRESH_TOKEN_KEY = "refreshToken";

type StoredAuthUser = {
  id: string;
  name: string;
  email: string;
  role: string;
  avatar?: string;
  status?: string;
  lastLoginAt?: string;
  createdAt?: string;
};

export type StoredSession = {
  token: string;
  refreshToken?: string | null;
  user: StoredAuthUser;
};

export function safeParseSession(raw: string | null): StoredSession | null {
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as Partial<StoredSession>;
    if (!parsed || typeof parsed !== "object") return null;
    if (!parsed.token || typeof parsed.token !== "string") return null;
    if (!parsed.user || typeof parsed.user !== "object") return null;
    if (!parsed.user.email || typeof parsed.user.email !== "string") {
      return null;
    }
    if (!parsed.user.id || typeof parsed.user.id !== "string") return null;
    if (!parsed.user.name || typeof parsed.user.name !== "string") return null;
    if (!parsed.user.role || typeof parsed.user.role !== "string") return null;

    return {
      token: parsed.token,
      refreshToken:
        typeof parsed.refreshToken === "string" ? parsed.refreshToken : null,
      user: parsed.user as StoredAuthUser,
    };
  } catch {
    return null;
  }
}

export function getStoredAccessToken(): string | null {
  return localStorage.getItem(ACCESS_TOKEN_KEY);
}

export function getStoredRefreshToken(): string | null {
  const token = localStorage.getItem(REFRESH_TOKEN_KEY);
  if (token) return token;

  return (
    safeParseSession(localStorage.getItem(AUTH_STORAGE_KEY))?.refreshToken ??
    null
  );
}

export function clearStoredSession(): void {
  localStorage.removeItem(AUTH_STORAGE_KEY);
  localStorage.removeItem(ACCESS_TOKEN_KEY);
  localStorage.removeItem(REFRESH_TOKEN_KEY);
}

export function persistStoredSession(session: StoredSession): void {
  localStorage.setItem(AUTH_STORAGE_KEY, JSON.stringify(session));
  localStorage.setItem(ACCESS_TOKEN_KEY, session.token);

  if (session.refreshToken) {
    localStorage.setItem(REFRESH_TOKEN_KEY, session.refreshToken);
  } else {
    localStorage.removeItem(REFRESH_TOKEN_KEY);
  }
}

export function updateStoredAccessToken(
  accessToken: string,
): StoredSession | null {
  localStorage.setItem(ACCESS_TOKEN_KEY, accessToken);

  const existing = safeParseSession(localStorage.getItem(AUTH_STORAGE_KEY));
  if (!existing) return null;

  const nextSession = {
    ...existing,
    token: accessToken,
  };
  localStorage.setItem(AUTH_STORAGE_KEY, JSON.stringify(nextSession));
  return nextSession;
}
